import React, {FC, StrictMode} from 'react';
import ReactDOM from 'react-dom';
import {Provider} from 'react-redux';
import {applyMiddleware} from 'redux';
import {Subject} from 'rxjs';

import {globalDocument, globalWindow} from '../libs/shared/helpers/src/browser/browserHelpers';
import {getWindow} from '../libs/shared/helpers/src/browser/domHelpers';
import {tryParseVisitorToken} from '../libs/shared/helpers/src/browser/urlHelpers';
import {BugsnagSeveritiesEnum} from '../libs/shared/helpers/src/bugsnag/bugsnagSeverities';
import {isProduction} from '../libs/shared/helpers/src/environment/envHelpers';
import {annotateTaskStep} from '../libs/shared/helpers/src/instrumentation/eventLoopPerformanceHelpers';
import {HandlerOf} from '../libs/shared/helpers/src/react/reactHelpers';
import {Layer} from '../libs/shared/ui/src/components/layers/layer';
import {SvgManager} from '../libs/shared/ui/src/components/svg/svgManager';
import {CssManager} from '../libs/shared/ui/src/css/cssManager';
import {RelativeDateTimeProvider} from '../srcCommon/formatters/dateTime/relativeDateTimeProvider';
import {InternationalizationProviderContainer} from '../srcCommon/internationalization/internationalizationProviderContainer';
import {bootAsync} from '../srcCommon/services/boot/boot';
import {reportToBugsnag, setupBugsnag} from '../srcCommon/services/bugsnag/bugsnag';
import {setupDesktopDatadogLogger} from '../srcCommon/services/logger/logger';
import {setupOfflineContent} from '../srcCommon/services/offline/offlineContent';
import {AppLoadMetricsEnum, logAppLoadMetric} from '../srcCommon/services/performanceMetrics/appLoadMetric';
import {setupEventLoopLogging} from '../srcCommon/services/performanceMetrics/eventLoopPerformance';
import {setupInteractionPerformanceLogging} from '../srcCommon/services/performanceMetrics/interactionPerformance';
import {reduxTimingMiddleware} from '../srcCommon/services/performanceMetrics/reduxPerformance';
import {noopPersistedStateProvider} from '../srcCommon/services/persistentState/noopPersistedStateProvider';
import {setupLocalPreferences} from '../srcCommon/services/storage/localPreferences';
import {AppContainer} from './components/appContainer';
import {LegacyApplicationsProviderContainer} from './components/applications/legacyApplicationsProviderContainer';
import {BannersProvider} from './components/common/banners/bannersProvider';
import {DragAndDropProvider} from './components/common/dragAndDropProvider';
import {InfoBubblesProviderContainer} from './components/common/infoBubbles/infoBubblesProviderContainer';
import {InteractionTrackingProvider} from './components/common/interactionTracking/interactionTrackingProvider';
import {KeyboardProviderContainer} from './components/common/keyboard/keyboardProviderContainer';
import {UserThemeProviderContainer} from './components/common/theme/userThemeProviderContainer';
import {ErrorReporterProvider} from './components/errorReporter/errorReporterProvider';
import {ErrorReporter} from './components/errors/errorReporter';
import {GrammarlyProvider} from './components/grammarly/grammarlyProvider';
import {GrammarlyWrapper} from './components/grammarly/grammarlyWrapper';
import {LegacyFrameRawBoot} from './components/legacy/legacyFrameHelpers';
import {ChatPushContainer} from './components/push/chatPushContainer';
import {PushContainer} from './components/push/pushContainer';
import {isVisitorTeammate} from './helpers/modelHelpers';
import {ensureSafeToLoadInWindow, hideLoadingOverlay} from './loadingHelpers';
import {createAppNavigationToHistoryMiddleware, setupAppNavigationFromHistory} from './services/appHistory';
import {redirectToSignin} from './services/browser';
import {setupBugsnagSessions} from './services/bugsnag/bugsnagSessions';
import {setupNodeFunctionMonitors} from './services/bugsnag/removeChildMonitor';
import {setupBuildVersion} from './services/buildVersion';
import {setupCalendarsRefresh} from './services/calendar/calendarsRefreshSubscription';
import {setupReminderNotifications} from './services/calendar/reminderNotificationsSubscription';
import {setupCountersMonitor} from './services/countersMonitorSubscription';
import {setupDarkModeTracking} from './services/darkModeTracking';
import {maybeSetupElectronBridge} from './services/electron/electronBridge';
import {maybeSetupElectronSubscription} from './services/electron/electronSubscription';
import {setupFeatureFlags} from './services/featureFlags';
import {setupGc} from './services/gc';
import {handleTimeMiddleware} from './services/handleTime';
import {setupHeartbeat} from './services/heartbeat';
import {registerHttpErrorHandlers} from './services/http/http';
import {setHttpToken, setupHttp} from './services/http/httpBase';
import {setupInboxesRefresh} from './services/inboxes/inboxesRefresh';
import {InvalidConversationTrackerMiddleware} from './services/invalidConversationTracking';
import {setupLogstash} from './services/logstash';
import {setupPendo} from './services/pendo';
import {performanceTimingMiddleware} from './services/performanceMetrics/legacyPerformanceMiddleware';
import {setupMemoryLogging} from './services/performanceMetrics/memoryLogging';
import {setupPrecogExpirations} from './services/precogs';
import {setupPrefetcher} from './services/prefetcher/prefetcher';
import {setupDatadogRealUserMonitoring} from './services/realUserMonitoring/datadogRealUserMonitoring';
import {
  initializeServiceWorker,
  setupServiceWorker,
} from './services/serviceWorker/bridge/setupServiceWorker';
import {setupStartGuide} from './services/startGuide';
import {setupTimezoneObserver} from './services/timezoneObserver';
import {setupTracking} from './services/tracking';
import {AppStore, createAppStore} from './store/appStore';
import {initializeCustomEmojiAction} from './store/data/customEmojis/customEmojiActions';
import {getWasOnboardingCompleted} from './store/data/preferences/preferencesSelectors';
import {getUser} from './store/data/teammates/teammatesSelectors';
import {makeGetAppPanel} from './store/panels/app/appPanelSelectors';
import {AppContentTypesEnum} from './store/panels/app/parameters/appParameters';
import {makeAppPanelId} from './store/panels/panelStateBase';
import {getHasWelcomeCarousel} from './store/stateless/welcomeCarouselSelectors';

// Handle errors from the very beginning.
setupBugsnag();
setupDesktopDatadogLogger();

// Avoid loading the app more than once in the same environment.
ensureSafeToLoadInWindow(globalWindow);

// Start logging boot fetch.
const onBootFetched = logAppLoadMetric(AppLoadMetricsEnum.BOOT_FETCH);

// Initialize service worker.
initializeServiceWorker();

// Configure Redux.
let store: AppStore;

// If a token was provided, set it on the HTTP service.
const visitorToken = tryParseVisitorToken(globalWindow.location.pathname);
setHttpToken(visitorToken);

// We need to setup the HTTP service before making a request.
registerHttpErrorHandlers({
  onUnauthorized: () => {
    // For visitors, nothing to do.
    if (visitorToken) {
      return;
    }

    // Otherwise, redirect to the signin page.
    redirectToSignin();
  },
});

// Establish a stream of raw boot blobs. This may be observed by the legacy frame.
const rawBoot$ = new Subject<LegacyFrameRawBoot>();
const onInitialRawBoot: HandlerOf<unknown> = (rawBoot) => rawBoot$.next({isInitial: true, rawBoot});
const onSubsequentRawBoot: HandlerOf<unknown> = (rawBoot) => rawBoot$.next({isInitial: false, rawBoot});

// Fetch the boot.
bootAsync(noopPersistedStateProvider)
  .then(({rawBoot: initialRawBoot, initialState}) => {
    onBootFetched();
    const onAppRendered = logAppLoadMetric(AppLoadMetricsEnum.APP_RENDER);
    annotateTaskStep('boot');
    const appPanelId = makeAppPanelId();

    // Create the store.
    const historyMiddleware = createAppNavigationToHistoryMiddleware(globalWindow, appPanelId);
    store = createAppStore({
      initialState,
      enhancer: applyMiddleware(
        historyMiddleware,
        reduxTimingMiddleware,
        performanceTimingMiddleware,
        handleTimeMiddleware,
        InvalidConversationTrackerMiddleware,
      ),
    });
    setupAppNavigationFromHistory(globalWindow, appPanelId, store.dispatch);

    const state = store.getState();
    const isUserVisitor = isVisitorTeammate(getUser(state));
    const appPanel = makeGetAppPanel()(state, {panelId: appPanelId});
    const isNotInStandaloneComposerMode = appPanel.contentType !== AppContentTypesEnum.STANDALONE_COMPOSER;

    // Setup our stateful services.
    setupNodeFunctionMonitors();
    setupHttp(store);
    setupBuildVersion(initialState.data.config);
    setupLocalPreferences(store);
    setupTracking(store);
    setupFeatureFlags(store);

    // Redirect to onboarding app if user is not a visitor, we're in prod, and they don't have the welcome carousel.
    if (
      isProduction() &&
      !getWasOnboardingCompleted(state) &&
      !getHasWelcomeCarousel(state) &&
      !isUserVisitor
    ) {
      globalWindow.location.href = '/get-started';
      return;
    }

    setupGc(store);
    setupLogstash(store);
    setupHeartbeat(store);
    setupDatadogRealUserMonitoring(store);
    setupPrecogExpirations(store);
    setupBugsnagSessions(store, appPanelId);
    setupEventLoopLogging(store, appPanelId);
    if (isNotInStandaloneComposerMode) {
      setupCountersMonitor(store);
    }
    setupServiceWorker(store);
    if (isNotInStandaloneComposerMode) {
      setupOfflineContent(store);
      setupReminderNotifications(store);
      setupInboxesRefresh(store);
      setupCalendarsRefresh(store);
      setupPrefetcher(store);
      setupStartGuide(store);
    }
    setupMemoryLogging(store);
    setupTimezoneObserver(store);
    if (isNotInStandaloneComposerMode) {
      setupPendo(store);
    }
    setupInteractionPerformanceLogging(store, getWindow);

    store.dispatch(initializeCustomEmojiAction());

    // Setup Electron bridge in native app context.
    maybeSetupElectronBridge(store);
    maybeSetupElectronSubscription(store);
    const dataState = store.getState().data;
    const {userId} = dataState;
    const companyId = dataState.company.id;

    const Providers: FC = ({children}) => (
      <InteractionTrackingProvider>
        <ErrorReporterProvider>
          <ErrorReporter userId={userId} companyId={companyId} isTopLevel>
            <DragAndDropProvider>
              <Provider store={store}>
                <UserThemeProviderContainer>
                  <RelativeDateTimeProvider>
                    <InternationalizationProviderContainer>
                      <BannersProvider>
                        <InfoBubblesProviderContainer panelId={appPanelId} store={store}>
                          <PushContainer onRawBoot={onSubsequentRawBoot}>
                            <ChatPushContainer>
                              <LegacyApplicationsProviderContainer>
                                <SvgManager>
                                  <KeyboardProviderContainer>
                                    <GrammarlyProvider>{children}</GrammarlyProvider>
                                  </KeyboardProviderContainer>
                                </SvgManager>
                              </LegacyApplicationsProviderContainer>
                            </ChatPushContainer>
                          </PushContainer>
                        </InfoBubblesProviderContainer>
                      </BannersProvider>
                    </InternationalizationProviderContainer>
                  </RelativeDateTimeProvider>
                </UserThemeProviderContainer>
              </Provider>
            </DragAndDropProvider>
          </ErrorReporter>
        </ErrorReporterProvider>
      </InteractionTrackingProvider>
    );

    let hasLoggedRender = false;

    // Start running the app.
    ReactDOM.render(
      <StrictMode>
        <Providers>
          <GrammarlyWrapper>
            <Layer window={globalWindow}>
              <CssManager>
                <AppContainer panelId={appPanelId} rawBoot$={rawBoot$} />
              </CssManager>
            </Layer>
          </GrammarlyWrapper>
        </Providers>
      </StrictMode>,
      globalDocument.getElementById('front-app-root'),
      () => {
        hideLoadingOverlay();

        if (!hasLoggedRender) {
          hasLoggedRender = true;
          onAppRendered(true);
        }
      },
    );

    // Consume the initial raw boot blob, and broadcast it to any listeners.
    if (initialRawBoot) {
      initialRawBoot.withValue(onInitialRawBoot);
    }

    setupDarkModeTracking();
  })
  .catch((error) => {
    if (error && error.status === 401) {
      // This is an unauthorized error. Let the handler above redirect to the signin page.
      return;
    }

    reportToBugsnag(error, {
      severity: BugsnagSeveritiesEnum.ERROR,
    });

    // Show error.
    const failedElement = globalDocument.getElementById('failed');
    if (failedElement) {
      failedElement.classList.add('show');
    }

    // Hide progress bar.
    const progressElement = globalDocument.getElementById('progress');
    if (progressElement) {
      progressElement.classList.add('hide');
    }
  });
